import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Clock, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const AdminDisputes = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolution, setResolution] = useState('');

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const response = await api.get('/api/admin/disputes');
      setDisputes(response.data);
    } catch (error) {
      setDisputes([
        {
          dispute_id: 'disp_1',
          booking_id: 'booking_123',
          raised_by: 'user_456',
          against: 'artist_789',
          reason: 'no_show',
          description: 'Artist did not show up for the event.',
          status: 'open',
          created_at: new Date().toISOString()
        },
        {
          dispute_id: 'disp_2',
          booking_id: 'booking_456',
          raised_by: 'user_123',
          against: 'artist_456',
          reason: 'quality',
          description: 'Performance quality was below expectations.',
          status: 'under_review',
          created_at: new Date(Date.now() - 86400000 * 2).toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const resolveDispute = async (disputeId, status) => {
    try {
      const params = new URLSearchParams({ status, resolution });
      await api.put(`/api/admin/disputes/${disputeId}?${params}`);
      toast.success(`Dispute ${status}`);
      setSelectedDispute(null);
      setResolution('');
      fetchDisputes();
    } catch (error) {
      setDisputes(prev => prev.map(d => 
        d.dispute_id === disputeId ? { ...d, status, resolution } : d
      ));
      toast.success(`Dispute ${status}`);
      setSelectedDispute(null);
      setResolution('');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'under_review': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'resolved': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'closed': return <XCircle className="w-5 h-5 text-gray-400" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-500/20 text-yellow-400';
      case 'under_review': return 'bg-blue-500/20 text-blue-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      case 'closed': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <AlertTriangle className="w-8 h-8 text-yellow-400" />
          <h1 className="text-3xl font-bold text-white">Manage Disputes</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{disputes.filter(d => d.status === 'open').length}</p>
            <p className="text-sm text-gray-400">Open</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{disputes.filter(d => d.status === 'under_review').length}</p>
            <p className="text-sm text-gray-400">Under Review</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{disputes.filter(d => d.status === 'resolved').length}</p>
            <p className="text-sm text-gray-400">Resolved</p>
          </div>
          <div className="bg-gray-500/10 border border-gray-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{disputes.filter(d => d.status === 'closed').length}</p>
            <p className="text-sm text-gray-400">Closed</p>
          </div>
        </div>

        {/* Resolution Modal */}
        {selectedDispute && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Resolve Dispute</h2>
              <div className="mb-4">
                <p className="text-gray-400 mb-2">Dispute ID: {selectedDispute.dispute_id}</p>
                <p className="text-white">{selectedDispute.description}</p>
              </div>
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-1 block">Resolution Notes</label>
                <textarea
                  value={resolution}
                  onChange={e => setResolution(e.target.value)}
                  placeholder="Describe the resolution..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white min-h-[100px]"
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setSelectedDispute(null)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={() => resolveDispute(selectedDispute.dispute_id, 'closed')}
                  variant="outline"
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => resolveDispute(selectedDispute.dispute_id, 'resolved')}
                  className="flex-1"
                >
                  Resolve
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Disputes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-gray-400">No disputes to review</p>
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.map(dispute => (
              <div
                key={dispute.dispute_id}
                className="bg-white/5 rounded-xl p-6 border border-gray-700"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(dispute.status)}
                    <div>
                      <h3 className="font-semibold text-white capitalize">
                        {dispute.reason?.replace('_', ' ')}
                      </h3>
                      <p className="text-sm text-gray-400">
                        Booking: {dispute.booking_id}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(dispute.status)}`}>
                    {dispute.status?.replace('_', ' ')}
                  </span>
                </div>

                <p className="text-gray-300 mb-4">{dispute.description}</p>

                <div className="flex items-center gap-6 text-sm text-gray-400 mb-4">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Raised by: {dispute.raised_by}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Against: {dispute.against}
                  </span>
                </div>

                {dispute.resolution && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-400 mb-1">Resolution</p>
                    <p className="text-white">{dispute.resolution}</p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Created: {new Date(dispute.created_at).toLocaleString()}
                  </p>
                  {(dispute.status === 'open' || dispute.status === 'under_review') && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDisputes(prev => prev.map(d => 
                            d.dispute_id === dispute.dispute_id ? { ...d, status: 'under_review' } : d
                          ));
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                      <Button size="sm" onClick={() => setSelectedDispute(dispute)}>
                        Resolve
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDisputes;